import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext, useMemo } from 'react';

import { NO_OPENINGS } from '@/features/storefront/api/careers';
import { NO_LOCATIONS } from '@/features/storefront/api/locations';
import { NO_TEAM } from '@/features/storefront/api/team';
import type { StorefrontProductSummary } from '@/features/storefront/api/storefront';
import {
  StorefrontQueryLanguageContext,
  useStorefrontData,
  storefrontScope,
} from '@/features/storefront/data/context';
import { liveSourceFor } from '@/features/storefront/data/live';
import { useStorefrontFeatures } from '@/features/storefront/features';
import { EMPTY_TEASER, fetchBlogTeaser } from '@/features/storefront/sections/blogTeaser';
import {
  productTabKey,
  type ProductQueryFactory,
} from '@/features/storefront/sections/productTabs';
import {
  fetchProductSlugIndex,
  mayBeSold,
  UnsoldProductError,
} from '@/features/storefront/sections/productSlugs';
import type { Section } from '@/features/storefront/sections/schema';
import type { ProductDetailQueryFactory } from '@/features/storefront/sections/LookbookSection';

/**
 * The catalog data a page of sections needs, fetched from what the page asks
 * for.
 *
 * One listing request covers every unpinned product section, and one more per
 * category that some section is pinned to - so the number of requests follows
 * how the merchant actually composed the page, and a pinned section shows that
 * category's products rather than whichever of them happened to land in the
 * first page of a single query.
 *
 * Shared by the live storefront and the dashboard's customiser. The customiser
 * passes `host`, because the dashboard's own hostname says nothing about which
 * store is being designed.
 */
/** Enough products for the largest grid a section may ask for. */
const PRODUCTS_PER_QUERY = 24;

export function useSectionData(sections: Section[], host?: string, eager = false) {
  const contextSource = useStorefrontData();
  // An explicit host is the dashboard asking about a store it already knows,
  // from outside any storefront shell. Everything else reads the shop it is
  // being rendered inside - the merchant's, or a theme's static catalog.
  const source = useMemo(
    () => (host ? liveSourceFor(host) : contextSource),
    [host, contextSource],
  );
  const language = useContext(StorefrontQueryLanguageContext);
  const scope = useMemo(
    // The dashboard's explicit host is outside any shell and keys as before.
    () => storefrontScope(source, host ? null : language),
    [source, host, language],
  );

  // What this page of sections actually needs. Every storefront page renders
  // sections now, and a checkout page that asked for the whole catalog to
  // render no product sections would be one wasted request per shopper at the
  // most expensive moment of their visit - and, in a test, one more call after
  // the one the test was watching for.
  const wantsProducts = eager || sections.some((section) => section.type === 'featured_products');
  const wantsCategories =
    eager ||
    // A link directory reads the category tree unless its groups are typed in.
    sections.some((section) => section.type === 'link_directory' && section.source !== 'manual') ||
    sections.some((section) => section.type === 'categories' || section.type === 'category_strip');
  // A brand strip with no logos of its own falls back to the brands on the
  // shop's products, which only the facet counts know.
  const wantsBrands = sections.some(
    (section) =>
      section.type === 'brand_strip' &&
      !section.blocks.some((block) => block.visible && block.image_url),
  );

  const categories = useQuery({
    queryKey: [...scope, 'categories'],
    queryFn: () => source.categories(),
    enabled: wantsCategories,
  });

  // `null` first: the unpinned sections' list, which any page with a product
  // section needs. Empty when no section on the page has one.
  const facets = useQuery({
    queryKey: [...scope, 'facets', 'brand_strip'],
    queryFn: () => source.facets({}),
    enabled: wantsBrands,
  });

  // The blog teaser's posts, only on a page that has one and a shop whose
  // blog is switched on. Its own key: the blog index pages through `posts`
  // twelve at a time, and a teaser's six must not answer for them.
  const features = useStorefrontFeatures();
  const wantsPosts = features.blog && sections.some((section) => section.type === 'blog_posts');
  const teaser = useQuery({
    queryKey: [...scope, 'blog-teaser'],
    queryFn: () => fetchBlogTeaser(source),
    enabled: wantsPosts,
    retry: false,
  });

  // The store locator's branches, only on a page that has one. A source that
  // cannot answer (an older stand-in) or a request that fails reads as none.
  const wantsLocations = sections.some((section) => section.type === 'store_locator');
  const locations = useQuery({
    queryKey: [...scope, 'store-locations'],
    queryFn: async () => (source.locations ? await source.locations() : NO_LOCATIONS),
    enabled: wantsLocations,
    retry: false,
  });

  // The shop's people, on a page with a team section or a bookings section
  // that books by person. Same tolerance as the locator: a source without
  // people, or a request that fails, reads as nobody.
  const wantsTeam = sections.some(
    (section) =>
      section.type === 'team' || (section.type === 'bookings' && section.staff_mode !== 'none'),
  );
  const team = useQuery({
    queryKey: [...scope, 'team-members'],
    queryFn: async () => (source.team ? await source.team() : NO_TEAM),
    enabled: wantsTeam,
    retry: false,
  });

  // The shop's open positions, on a page with a careers section and a shop
  // with its Careers page on - off, the server answers 404 anyway. Same
  // tolerance as the team: no source, or a failed request, reads as none.
  const wantsCareers = features.careers && sections.some((section) => section.type === 'careers');
  const careers = useQuery({
    queryKey: [...scope, 'careers'],
    queryFn: async () => (source.careers ? await source.careers() : NO_OPENINGS),
    enabled: wantsCareers,
    retry: false,
  });

  const pinned = useMemo(() => {
    if (!wantsProducts) return [];
    const ids = sections
      .filter((section) => section.type === 'featured_products' && section.category_id)
      .map((section) => (section as { category_id: string }).category_id);
    return [null, ...new Set(ids)];
  }, [sections, wantsProducts]);

  const results = useQueries({
    queries: pinned.map((categoryId) => ({
      queryKey: [...scope, 'products', categoryId ?? 'all'],
      queryFn: () => source.products({ size: PRODUCTS_PER_QUERY, categoryId }),
    })),
  });

  const byCategory = useMemo(() => {
    const map = new Map<string | null, StorefrontProductSummary[]>();
    pinned.forEach((categoryId, index) => {
      map.set(categoryId, results[index]?.data?.items ?? []);
    });
    return map;
  }, [pinned, results]);

  const productsFor = useCallback(
    (categoryId: string | null) => byCategory.get(categoryId) ?? [],
    [byCategory],
  );

  // A product tab's request, which the tab itself runs - and only once it is
  // opened - so a row of four tabs costs one request until a shopper clicks.
  const productQuery = useCallback<ProductQueryFactory>(
    (query) => ({
      queryKey: productTabKey(scope, query),
      queryFn: async () =>
        (
          await source.products({
            size: query.size,
            sort: query.sort,
            onSale: query.onSale,
            categoryId: query.categoryId,
          })
        ).items,
    }),
    [scope, source],
  );

  // A lookbook hotspot's product in full, under the product page's own key -
  // so a shopper who opens it next finds it already fetched.
  //
  // A live shop first checks the slug is one it sells (`productSlugs.ts`): a
  // theme from the platform catalog names its designer's sample products, and
  // each of those would otherwise be a 404 on every visit.
  const queryClient = useQueryClient();
  const productDetailQuery = useCallback<ProductDetailQueryFactory>(
    (slug) => ({
      queryKey: [...scope, 'product', slug],
      queryFn: async () => {
        if (!source.preview) {
          const index = await queryClient.fetchQuery({
            queryKey: [...scope, 'product-slugs'],
            queryFn: () => fetchProductSlugIndex(source),
            staleTime: 60_000,
          });
          if (!mayBeSold(index, slug)) throw new UnsoldProductError(slug);
        }
        return source.product(slug);
      },
    }),
    [scope, source, queryClient],
  );

  return {
    productsFor,
    productQuery,
    productDetailQuery,
    categories: categories.data ?? [],
    brands: facets.data?.brands ?? [],
    blogTeaser: teaser.data ?? EMPTY_TEASER,
    // `null` while on its way, so the section can tell "loading" from "none".
    storeLocations: locations.isError ? NO_LOCATIONS : (locations.data ?? null),
    // The same: `null` while on its way.
    team: team.isError ? NO_TEAM : (team.data ?? null),
    // The same: `null` while on its way.
    careers: careers.isError ? NO_OPENINGS : (careers.data ?? null),
    // A query that was never enabled reports `isPending` forever, so each half
    // counts only while it is something this page asked for. A page with no
    // catalog on it is not loading a catalog.
    loading:
      (wantsCategories && categories.isPending) || results.some((result) => result.isPending),
  };
}
